<template>
  <div class="content-elements">
    <div class="content-elements__control-panel-wrapper">
      <content-control-panel :hideRefresh="hideRefresh" 
        :predefinedSettings="settings" 
        :isSimplifiedView="isSimplifiedView"
        @settings="onSettingsChanged" />
      <div v-if="hideFields && showRecommendedField"
           class="content-elements__edit-meta-data"
           :class="{'content-elements__edit-meta-data_simplified': isSimplifiedView}"
           @click="openEditModal">
        <base-material-design-icon icon="edit" />
        edit metadata
      </div>
    </div>

    <div class="content-elements__row" :class="{ 'content-elements__row-reverse': isReversRow}">
      <div v-if="showRecommendedField" :class="{'content-elements__column': isShowFieldAll, 'content-elements__column_simplified': isSimplifiedView}">
        <div class="content-elements__subtitle" :style="subtitleStyles">
          <span v-if="!hideLabels && !isSimplifiedView">{{ recommendedLabel }}</span>
          <GoogleSerpSimulatorLength
            :title="recommendedTitle"
            :desc="recommendedDescription"
          />
        </div>
        <google-serp-simulator
          class="content-elements__serp"
          :class="{'content-elements__serp-static': staticHeight}"
          :url="truncatedUrl"
          :fullUrl="content.published_url || content.url"
          :title="recommendedTitle"
          :description="recommendedDescription"
          :rating="settings.rating"
          :reviewCount="settings.reviewCount"
          :isReviewVisible="settings.isReviewVisible"
          :mode="settings.mode"
        />
      </div>

      <div v-if="showPublishedField && !isSimplifiedView" :class="{'content-elements__column': isShowFieldAll}">
        <div class="content-elements__subtitle" :style="subtitleStyles">
          <span v-if="!hideLabels">{{ currentLabel }}</span>
          <GoogleSerpSimulatorLength
            :title="content.tag_title_current"
            :desc="content.tag_meta_description_current"
          />
        </div>
        <google-serp-simulator
          v-if="isCurrentVisible"
          class="content-elements__serp"
          :class="{'content-elements__serp-static': staticHeight}"
          :url="truncatedUrl"
          :fullUrl="content.published_url || content.url"
          :title="content.tag_title_current"
          :description="content.tag_meta_description_current"
          :rating="settings.rating"
          :reviewCount="settings.reviewCount"
          :isReviewVisible="settings.isReviewVisible"
          :mode="settings.mode"/>
        <div v-else
             class="content-elements__serp content-elements__unpublished">
          Unpublished
        </div>
      </div>
    </div>

    <template v-if="!hideFields">
      <div class="content-elements__row" style="margin-top: 20px;">
        <RecommendedContent
          title="Recommended SEO Title Tag"
          class="content-elements__recommended content-elements__column"
          max-length="60"
          orangeZone="65"
          min-length="20"
          type="title"
          single-line
          v-model="recommendedTitle"/>
        <RecommendedContent
          class="content-elements__recommended content-elements__column"
          title="Recommended H1"
          type="h1"
          max-length="160"
          orangeZone="170"
          min-length="100"
          single-line
          v-model="recommendedH1"/>
      </div>
      <RecommendedContent
        class="content-elements__recommended"
        title="Recommended Description"
        type="description"
        max-length="160"
        orangeZone="170"
        min-length="100"
        v-model="recommendedDescription"/>
      <div class="content-elements__controls" v-if="!isCurrentUserClientCollaborator">
        <button class="content-elements__button content-elements__button_cancel"
                :class="{'content-elements__button_disabled': !isButtonsEnabled}"
                @click.prevent="cancel()">Revert Changes
        </button>
        <button class="content-elements__button content-elements__button_submit"
                :class="{'content-elements__button_disabled': !isButtonsEnabled}"
                @click.prevent="apply()">Save
        </button>
      </div>
    </template>

    <div class="content-elements__editor-container__wrapper"
        :class="{'content-elements__editor-container__wrapper-drag': editorDrag}"
        v-if="isEditorOpened">
      <VueDraggableResizable
        :draggable="activeResizable"
        ref="editor"
        @dragging="editorDrag = true"
        @dragstop="editorDrag = false"
        classNameResizing="content-elements__resizing"
        class-name="content-elements__editor-container"
        :class="{'content-elements__editor-resize-disabled': !activeResizable}"
        parent
        :h="700"
        :w="700"
        :min-height="250"
        :min-width="500"
      >
        <div class="content-elements__edit-metadata-wrapper">
          <EditMetaData :content="content"
                        :recommendedTitleKey="recommendedTitleKey"
                        :recommendedDescriptionKey="recommendedDescriptionKey"
                        @close="editModal"/>
        </div>
      </VueDraggableResizable>
    </div>
  </div>
</template>

<script>
/* eslint-disable no-unused-vars */

import RecommendedContent from './RecommendedContent'
import filters from '@/common/Filters'
import { mapActions } from 'vuex'
import ContentControlPanel from './ContentControlPanel'
import GoogleSerpSimulator from './GoogleSerpSimulator'
import VueDraggableResizable from 'vue-draggable-resizable'
import GoogleSerpSimulatorLength from './GoogleSerpSimulatorLength'
import EditMetaData from './EditMetaData'
import StrategyTab from './StrategyTab'
import baseModalProps from '@/common/BaseModalProps'

const defaultSettings = {
  rating: 4,
  reviewCount: 50
}
/**
 * Component is used in {@link StrategyTab}
 * It shows SERP data and recommended data
 */
export default {
  name: 'ContentElements',
  props: {
    content: {
      type: Object,
      required: true
    },
    subtitleStyles: {
      type: Object,
      default: () => ({})
    },
    hideFields: {
      type: Boolean,
      default: false
    },
    hideLabels: {
      type: Boolean,
      default: false
    },
    hideRefresh: {
      type: Boolean,
      default: false
    },
    isReversRow: {
      type: Boolean,
      default: false
    },
    staticHeight: {
      type: Boolean,
      default: false
    },
    currentLabel: {
      type: String,
      default: 'Published'
    },
    showField: {
      type: String,
      default: 'all'
    },
    recommendedLabel: {
      type: String,
      default: 'Optimized'
    },
    recommendedTitleKey: {
      type: String,
      default: 'tag_title_optimized'
    },
    recommendedDescriptionKey: {
      type: String,
      default: 'tag_meta_description_optimized'
    },
    recommendedH1Key: {
      type: String,
      default: 'tag_h1_recommended'
    },
    isSimplifiedView: Boolean
  },
  data () {
    return {
      recommendedTitle: '',
      recommendedDescription: '',
      recommendedH1: '',
      settings: {},
      isEditorOpened: false,
      activeResizable: true,
      editorDrag: false,
      defaultSettings
    }
  },
  watch: {
    content: {
      handler (value) {
        if (value) {
          this.recommendedTitle = value[this.recommendedTitleKey]
          this.recommendedDescription = value[this.recommendedDescriptionKey]
          this.recommendedH1 = value[this.recommendedH1Key]
        }
      },
      immediate: true
    }
  },
  components: {
    EditMetaData,
    GoogleSerpSimulator,
    GoogleSerpSimulatorLength,
    ContentControlPanel,
    VueDraggableResizable,
    RecommendedContent
  },
  computed: {
    isCurrentVisible () {
      return this.content.url_status === 'Published' && this.content.tag_title_current
    },
    showPublishedField () {
      return this.isShowFieldAll || this.showField === 'published'
    },
    showRecommendedField () {
      return this.isShowFieldAll || this.showField === 'recommended'
    },
    isShowFieldAll () {
      return this.showField === 'all'
    },
    isButtonsEnabled () {
      const isDescriptionChanged = this.recommendedDescription !== this.content[this.recommendedDescriptionKey]
      const isTitleChanged = this.recommendedTitle !== this.content[this.recommendedTitleKey]
      const isH1Changed = this.recommendedH1 !== this.content[this.recommendedH1Key]
      return isDescriptionChanged || isH1Changed || isTitleChanged
    },
    truncatedUrl () {
      if (!this.content.url) {
        return this.content.url
      }
      // remove protocol here
      const truncateUrl = this.content.url.replace(/(^\w+:|^)\/\//, '')
      if (truncateUrl.length <= 60) {
        return truncateUrl
      }
      const splitedUrl = truncateUrl.split('/')
      const lastSymbolsCount = 57 - splitedUrl[0].length
      return `${splitedUrl[0]}...${truncateUrl.slice(truncateUrl.length - lastSymbolsCount)}`
    }
  },
  methods: {
    ...mapActions('briefContent', ['changeContentBrief']),
    onSettingsChanged (settings) {
      this.settings = settings
    },
    apply () {
      const tag_meta_description_recommended = this.recommendedDescription !== this.content[this.recommendedDescriptionKey] ? this.recommendedDescription : null
      const tag_title_recommended = this.recommendedTitle !== this.content[this.recommendedTitleKey] ? this.recommendedTitle : null
      const tag_h1_recommended = this.recommendedH1 !== this.content[this.recommendedH1Key] ? this.recommendedH1 : null
      const payload = {}
      if (typeof tag_meta_description_recommended === 'string') {
        payload[this.recommendedDescriptionKey] = tag_meta_description_recommended
      }
      if (typeof tag_title_recommended === 'string') {
        payload[this.recommendedTitleKey] = tag_title_recommended
      }
      if (typeof tag_h1_recommended === 'string') {
        payload[this.recommendedH1Key] = tag_h1_recommended
      }
      this.$emit('apply', { clientId: this.clientId, briefId: this.content._id, ...payload })
    },
    cancel () {
      this.recommendedTitle = this.content[this.recommendedTitleKey]
      this.recommendedDescription = this.content[this.recommendedDescriptionKey]
      this.recommendedH1 = this.content[this.recommendedH1Key]
    },
    async openEditModal () {
      this.isEditorOpened = true
      await this.$nextTick()
      const resizableElement = this.$refs.editor.$el.querySelector('.edit-meta-data')
      const height = resizableElement.clientHeight <= window.innerHeight
        ? resizableElement.clientHeight
        : window.innerHeight - 60
      this.$refs.editor.$el.setAttribute('style', `
        top: ${(window.innerHeight - height) / 2}px;
        left: 200px;
        position: fixed;
        width: 50vw;
        height: ${height}px;
        max-height: 100vh;
      `)
      this.$refs.editor.x = this.$refs.editor.$el.offsetLeft
      this.$refs.editor.top = this.$refs.editor.$el.offsetTop
      this.$refs.editor.w = this.$refs.editor.$el.offsetWidth
      this.$refs.editor.h = height
    },
    editModal (data) {
      this.isEditorOpened = false
      if (data) {
        Object.assign(this, data)
        this.apply()
      }
    }
  },
  filters: {
    ...filters
  }
}
</script>
<style lang="scss">
  .content-elements {
    width: -webkit-fill-available;
    padding: 0 24px 24px;

    &__editor-container__wrapper {
      z-index: 1000;
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      pointer-events: none;
      user-select: none;
    }

    &__editor-container__wrapper-drag {
      pointer-events: all !important;
    }

    &__editor-resize-disabled {
      .handle {
        pointer-events: none !important;
        cursor: default !important;
      }
    }

    &__resizing {

      .strategy-tab__editor {
        pointer-events: none !important;
        user-select: none !important;
      }
    }
    &__edit-metadata-wrapper {
      overflow: scroll;
      height: -webkit-fill-available;
    }

    &__editor-container {
      background-color: #fff;
      box-shadow: 0 0 10px 27px rgba(0,0,0,0.1);;
      z-index: 999;
      pointer-events: all;

      &_shifted {
        width: 78vw;
        margin-left: 100px;
      }

      .handle {
        position: absolute;
        z-index: 1000;
        display: block !important;
      }

      .handle-tm {
        height: 6px;
        width: 98.6%;
        left: 6px;
        cursor: ns-resize;
      }

      .handle-bm {
        height: 6px;
        width: 98.6%;
        left: 6px;
        cursor: ns-resize;
        bottom: 0;
      }

      .handle-tl {
        height: 12px;
        width: 6px;
        cursor: nwse-resize;
      }

      .handle-bl {
        height: 12px;
        width: 6px;
        cursor: nesw-resize;
        bottom: 0;
      }

      .handle-tr {
        right: 0;
        height: 12px;
        width: 6px;
        cursor: nesw-resize;
      }

      .handle-br {
        right: 0;
        height: 12px;
        width: 6px;
        bottom: 0;
        cursor: nwse-resize;
      }

      .handle-ml, .handle-mr {
        top: 12px;
        height: 95.2%;
        width: 6px;
        cursor: ew-resize;
      }

      .handle-mr {
        right: 0;
      }
    }

    &__editor {

    }
    &__editor-drag {
      pointer-events: none;
      user-select: none;

      .content-notes-editor__wrapper {
        user-select: none !important;
      }
    }

    &__edit-meta-data {
      text-transform: uppercase;
      cursor: pointer;
      color: $rfx-dark-march;
      font-size: 10px;
      line-height: 24px;
      letter-spacing: 1.25px;
      font-weight: 900;
      display: flex;
      align-items: center;
      margin: 24px auto 0 24px;

      &_simplified {
        margin: 24px 0 0 auto;
      }

      span {
        font-size: 16px;
        margin-right: 12px;
      }
    }

    &__control-panel-wrapper {
      display: flex;
    }

    &__unpublished {
      display: flex;
      justify-content: center;
      align-items: center;
      width: -webkit-fill-available;
      border: 1px solid #d4d6e6;
      border-radius: 4px;
      padding: 20px;
      font-size: 14px;
      color: $rfx-light-march;
      height: 93px;
    }

    &__row {
      display: flex;

      &-reverse {
        flex-direction: row-reverse;

        .content-elements__column {
          &:last-child {
            margin-right: auto;
          }
          &:first-child {
            margin-right: 0;
          }
        }
      }

      & > div {
        width: -webkit-fill-available;
      }
    }

    &__column {
      display: flex;
      flex-direction: column;
      width: calc(50% - 12px) !important  ;

      &:first-child {
        margin-right: auto;
      }

      .content-elements__serp {
        margin-top: 0 !important;
      }

      &_simplified {
        width: 100% !important;
      }
    }

    &__title {
      font-size: 12px;
      font-weight: 500;
      color: $rfx-dark-march;
    }

    &__serp {
      margin-top: 24px;
      flex: 1;
      max-width: none;
      width: -webkit-fill-available;

      &-static {
        height: 93px !important;
      }
    }

    &__recommended {
      margin-top: 30px;
    }

    &__subtitle {
      margin: 24px 8px 12px 0;
      font-weight: 500;
      color: $rfx-dark-march;
    }
     &__controls {
      display: flex;
      justify-content: flex-end;
      margin-top: 30px;
    }
    &__button {
      width: 100px;
      height: 40px;
      border-radius: 4px;
      font-size: 10px;
      font-weight: 900;
      text-transform: uppercase;
      margin-left: 30px;
      border: none;
      outline: none;
      cursor: pointer;

      &_disabled {
        pointer-events: none;
        cursor: default;
        opacity: .4;
      }

      &_submit {
        background-color: $rfx-medium-march;
        color: #fff;
      }
      &_cancel {
        color: $rfx-dark-march;
        background-color: #fff;
      }
      &_disabled {
        pointer-events: none;
        background-color: #fff;
        color: $rfx-neutral-grey;
        cursor: default;
      }
    }
  }

</style>
